import React, { Component } from 'react'
import { Col, Row } from 'react-bootstrap';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Paper } from "@mui/material";
import { sumMoney, formatAmount } from './../../helper';
import SectionHeader from '../../Pages/admin/shared/SectionHeader';
import { NotApprovedRange } from '../../Pages/admin/shared/NotApprovedTag';

/* Account Balances — Summary view.

   One row per pay account, mirroring the legacy report's columns:
   Account | Balance B/F | P&L | Card | Shop Pay | Account Pay | Net Balance
   (legacy: SP_MS_FMS_RptAccountTransBalance_Summary_Query, rendered by
   Reports/MS_RPT_AccountBalance.aspx in "By Summary" mode).

   Rows are built in index.js by running the SAME per-account computation the
   Detail view uses, so a figure here can never disagree with the Detail footer.
   This component only formats and totals — it does no arithmetic of its own
   beyond summing the column. */

const headCell = { background: '#f1f1f1', color: '#111827', fontWeight: 600 };
const numCell = { textAlign: 'right' };
const footCell = { fontWeight: 800, textAlign: 'right', color: '#111827', background: '#ffefd6' };

class Summary extends Component {

    render() {
        const { TotalSummary = [], WeekRange = "", UnapprovedWeeks = [] } = this.props;

        // Every column totals straight down, Net included — the sum of the per-account
        // nets is the group's net position, so no separate formula is needed here.
        // Summing the *displayed* 2dp mirrors means the footer always equals the column
        // of figures above it, penny for penny.
        const total = (field) => formatAmount(sumMoney(TotalSummary, field));

        // A failed account contributes nothing, which would make the Total quietly
        // understated. Say so rather than presenting an incomplete figure as final.
        const failedCount = TotalSummary.filter(r => r.failed).length;

        return (
            <section>
                <SectionHeader title={<>{WeekRange}<NotApprovedRange weeks={UnapprovedWeeks} /></>} />

                <Row>
                    <Col md={12} >
                        <Paper>
                            <TableContainer className="report-table-container" >
                                <Table className="report-table pc-shrink-table" size="small" stickyHeader aria-label="account balances summary">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={headCell} >Account</TableCell>
                                            <TableCell style={{ ...headCell, ...numCell }} >Balance B/F</TableCell>
                                            <TableCell style={{ ...headCell, ...numCell }} >P&L</TableCell>
                                            <TableCell style={{ ...headCell, ...numCell }} >Card</TableCell>
                                            <TableCell style={{ ...headCell, ...numCell }} >Shop Pay</TableCell>
                                            <TableCell style={{ ...headCell, ...numCell }} >Account Pay</TableCell>
                                            <TableCell style={{ ...headCell, ...numCell }} >Net Balance</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {TotalSummary.length === 0 &&
                                            <TableRow>
                                                <TableCell colSpan={7} style={{ textAlign: 'center' }} >
                                                    There were no results for matching criteria
                                                </TableCell>
                                            </TableRow>
                                        }

                                        {TotalSummary.map((row, idx) => (
                                            <TableRow key={idx} >
                                                <TableCell>
                                                    {row.AccountTitle}
                                                    {/* A single account failing its fetch must not silently read as 0.00. */}
                                                    {row.failed &&
                                                        <span style={{ color: '#a32f2f', fontSize: '11px', marginLeft: '6px' }} >
                                                            (could not be loaded)
                                                        </span>
                                                    }
                                                </TableCell>
                                                {/* Pre-formatted in index.js so these read identically
                                                    to the Detail footer — do not re-round here. */}
                                                <TableCell style={numCell} >{row.failed ? '—' : formatAmount(row.FBtxt)}</TableCell>
                                                <TableCell style={numCell} >{row.failed ? '—' : formatAmount(row.PnLtxt)}</TableCell>
                                                <TableCell style={numCell} >{row.failed ? '—' : formatAmount(row.Cardtxt)}</TableCell>
                                                <TableCell style={numCell} >{row.failed ? '—' : formatAmount(row.ShopPaytxt)}</TableCell>
                                                <TableCell style={numCell} >{row.failed ? '—' : formatAmount(row.AccountPaytxt)}</TableCell>
                                                <TableCell style={{ ...numCell, fontWeight: 700 }} >
                                                    {row.failed ? '—' : formatAmount(row.Nettxt)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>

                                    {TotalSummary.length > 0 &&
                                        <TableFooter>
                                            <TableRow>
                                                <TableCell style={{ ...footCell, textAlign: 'left' }} >Total</TableCell>
                                                <TableCell style={footCell} >{total('FB')}</TableCell>
                                                <TableCell style={footCell} >{total('PnL')}</TableCell>
                                                <TableCell style={footCell} >{total('Card')}</TableCell>
                                                <TableCell style={footCell} >{total('ShopPay')}</TableCell>
                                                <TableCell style={footCell} >{total('AccountPay')}</TableCell>
                                                <TableCell style={footCell} >{total('Net')}</TableCell>
                                            </TableRow>
                                        </TableFooter>
                                    }
                                </Table>
                            </TableContainer>
                        </Paper>

                        {failedCount > 0 &&
                            <div style={{ color: '#a32f2f', fontSize: '12px', padding: '8px 4px' }} >
                                {failedCount} account{failedCount === 1 ? '' : 's'} could not be loaded and
                                {failedCount === 1 ? ' is' : ' are'} excluded from the Total — re-run the report.
                            </div>
                        }
                    </Col>
                </Row>
            </section>
        );
    }
}

export default Summary;
